import { useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuthContext } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useNotifications } from '@/hooks/useNotifications';

const pageTitles: Record<string, { title: string; subtitle: string }> = {
  '/dashboard': { title: 'Dashboard', subtitle: 'Platform overview and key metrics' },
  '/products': { title: 'Products', subtitle: 'Manage your API product catalog' },
  '/orders': { title: 'Orders', subtitle: 'Track and manage customer orders' },
  '/payments': { title: 'Payments', subtitle: 'Crypto payment transactions via NOWPayments' },
  '/subscriptions': { title: 'Subscriptions & API Keys', subtitle: 'Active user subscriptions and access keys' },
  '/admin': { title: 'Admin CMS', subtitle: 'Full control panel for platform management' },
  '/developer': { title: 'Developer Hub', subtitle: 'API keys, docs, testing panel and usage analytics' },
  '/account': { title: 'My Account', subtitle: 'Manage your profile, password and security settings' },
  '/support': { title: 'Support Center', subtitle: 'Submit and track your support tickets' },
};

const notifTypeIcon: Record<string, string> = {
  order_paid: 'ri-checkbox-circle-line',
  order_failed: 'ri-close-circle-line',
  order_new: 'ri-shopping-cart-line',
  info: 'ri-information-line',
};

const notifTypeColor: Record<string, string> = {
  order_paid: 'text-green-500',
  order_failed: 'text-red-500',
  order_new: 'text-brand-500',
  info: 'text-gray-400',
};

export default function TopBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const page = pageTitles[location.pathname] || { title: 'API MART', subtitle: '' };
  const [notifOpen, setNotifOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { user, logout } = useAuthContext();
  const { profile } = useProfile();
  const { notifications, unreadCount, liveToast, markAllRead, isAdmin } = useNotifications();

  const displayName = user?.username
    ? user.username.charAt(0).toUpperCase() + user.username.slice(1)
    : 'User';
  const displayEmail = user?.email ?? '';
  const initials = displayName.slice(0, 2).toUpperCase();
  const avatarUrl = profile?.avatar_url ?? null;

  const handleLogout = async () => {
    await logout();
    setUserOpen(false);
    navigate('/');
  };

  const closeAll = () => {
    setNotifOpen(false);
    setUserOpen(false);
  };

  const handleNotifOpen = () => {
    setNotifOpen(!notifOpen);
    setUserOpen(false);
    if (!notifOpen) markAllRead();
  };

  return (
    <>
      {/* Live order toast — admin only */}
      <AnimatePresence>
        {liveToast && isAdmin && (
          <motion.div
            initial={{ opacity: 0, y: -56, x: 0 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -56 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="fixed top-4 right-4 z-[100] bg-sidebar text-white px-4 py-3 rounded-xl flex items-center gap-3 max-w-xs"
          >
            <span className="w-8 h-8 bg-brand-500/20 rounded-lg flex items-center justify-center shrink-0">
              <i className="ri-shopping-cart-line text-brand-400 text-base" />
            </span>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold text-brand-400 uppercase tracking-wider mb-0.5">New Order</div>
              <div className="text-xs text-white/80 truncate">{liveToast.text}</div>
            </div>
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse shrink-0" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop */}
      {(notifOpen || userOpen) && (
        <div className="fixed inset-0 z-30" onClick={closeAll} />
      )}

      <header className="h-[72px] bg-white border-b border-surface-3 flex items-center justify-between px-6 shrink-0 z-40 relative">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 leading-tight">{page.title}</h1>
          <p className="text-xs text-gray-400">{page.subtitle}</p>
        </div>

        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative hidden md:flex items-center">
            <i className="ri-search-line absolute left-3 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Search..."
              className="pl-9 pr-4 py-1.5 text-sm bg-surface-2 border border-surface-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent w-52"
            />
          </div>

          {/* Notifications */}
          <div className="relative z-50">
            <button
              onClick={handleNotifOpen}
              className="relative w-9 h-9 flex items-center justify-center rounded-lg hover:bg-surface-2 transition-colors cursor-pointer"
            >
              <i className="ri-notification-3-line text-gray-600 text-lg" />
              {unreadCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-brand-500 rounded-full flex items-center justify-center"
                >
                  <span className="text-[9px] font-bold text-white leading-none">{unreadCount > 9 ? '9+' : unreadCount}</span>
                </motion.span>
              )}
            </button>

            <AnimatePresence>
              {notifOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-12 w-80 bg-white border border-surface-3 rounded-xl overflow-hidden z-50"
                >
                  <div className="px-4 py-3 border-b border-surface-3 flex justify-between items-center">
                    <span className="font-semibold text-sm">Notifications</span>
                    {isAdmin && notifications.length > 0 && (
                      <button
                        onClick={markAllRead}
                        className="text-[11px] text-brand-500 hover:text-brand-600 cursor-pointer whitespace-nowrap"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <i className="ri-notification-off-line text-gray-300 text-2xl block mb-2" />
                        <p className="text-xs text-gray-400">No notifications yet</p>
                      </div>
                    ) : (
                      notifications.map(n => (
                        <div
                          key={n.id}
                          className={`px-4 py-3 flex gap-3 border-b border-surface-3 last:border-0 transition-colors ${!n.read ? 'bg-brand-50' : ''}`}
                        >
                          <div className={`w-5 h-5 flex items-center justify-center mt-0.5 shrink-0 ${notifTypeColor[n.type]}`}>
                            <i className={`${notifTypeIcon[n.type]} text-base`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-700 leading-relaxed">{n.text}</p>
                            <p className="text-[11px] text-gray-400 mt-0.5">{n.time}</p>
                          </div>
                          {!n.read && (
                            <div className="w-1.5 h-1.5 rounded-full bg-brand-500 mt-1.5 shrink-0" />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  {isAdmin && (
                    <div className="px-4 py-2.5 border-t border-surface-3">
                      <button
                        onClick={() => { navigate('/orders'); setNotifOpen(false); }}
                        className="w-full text-center text-xs text-brand-500 hover:text-brand-600 cursor-pointer"
                      >
                        View all orders <i className="ri-arrow-right-s-line" />
                      </button>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* User menu */}
          <div className="relative z-50">
            <button
              onClick={() => { setUserOpen(!userOpen); setNotifOpen(false); }}
              className="flex items-center gap-2 pl-3 border-l border-surface-3 cursor-pointer group"
            >
              {/* Avatar */}
              <div className="w-8 h-8 rounded-full bg-sidebar flex items-center justify-center shrink-0 overflow-hidden">
                {avatarUrl ? (
                  <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white text-xs font-bold">{initials}</span>
                )}
              </div>
              <div className="hidden md:block text-left">
                <div className="text-xs font-semibold text-gray-800 group-hover:text-gray-900 transition-colors">
                  {displayName}
                </div>
                <div className="text-[11px] text-gray-400 max-w-[140px] truncate">
                  {displayEmail}
                </div>
              </div>
              <i className={`ri-arrow-down-s-line text-gray-400 text-base transition-transform hidden md:block ${userOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {userOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-12 w-56 bg-white border border-surface-3 rounded-xl overflow-hidden z-50"
                >
                  {/* Profile header */}
                  <div className="px-4 py-3.5 border-b border-gray-100 bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-sidebar flex items-center justify-center shrink-0 overflow-hidden">
                        {avatarUrl ? (
                          <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-white text-sm font-bold">{initials}</span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">{displayName}</div>
                        <div className="text-[11px] text-gray-400 truncate">{displayEmail}</div>
                      </div>
                    </div>
                    {user?.role === 'admin' && (
                      <span className="mt-2 inline-flex items-center gap-1 text-[10px] bg-brand-100 text-brand-700 px-2 py-0.5 rounded-full font-semibold">
                        <i className="ri-shield-star-line" />Admin
                      </span>
                    )}
                  </div>

                  <div className="py-1">
                    <button onClick={() => { setUserOpen(false); navigate('/dashboard'); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors">
                      <i className="ri-dashboard-line text-gray-400 text-base" />Dashboard
                    </button>
                    <button onClick={() => { setUserOpen(false); navigate('/account'); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors">
                      <i className="ri-user-settings-line text-gray-400 text-base" />My Account
                    </button>
                    <button onClick={() => { setUserOpen(false); navigate('/developer'); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors">
                      <i className="ri-terminal-line text-gray-400 text-base" />Developer Hub
                    </button>
                  </div>

                  <div className="border-t border-gray-100 py-1">
                    <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 cursor-pointer transition-colors">
                      <i className="ri-logout-box-r-line text-base" />Sign Out
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </header>

      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" />
    </>
  );
}
